import type { BcStoreUserRecord } from '../services/api';
import { buildPermissionMatrix, matrixCellValue } from './permissions-audit-matrix';
import type { PermissionMatrixRow } from './permissions-audit-matrix';

function csvEscape(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function triggerDownload(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
}

export function buildDetailCsvFromUsers(users: BcStoreUserRecord[]): string {
  const headers = [
    'store_hash',
    'store_name',
    'user_id',
    'email',
    'first_name',
    'last_name',
    'status',
    'permission_code',
    'access_type',
    'last_login_at',
    'updated_at',
  ];

  const rows: string[] = [];

  for (const user of users) {
    if (user.permissions.length === 0) {
      rows.push(
        [
          csvEscape(user.storeHash),
          csvEscape(user.storeName),
          csvEscape(user.id),
          csvEscape(user.email),
          csvEscape(user.firstName),
          csvEscape(user.lastName),
          csvEscape(user.status),
          '',
          'none',
          csvEscape(user.lastLoginAt),
          csvEscape(user.updatedAt),
        ].join(','),
      );
      continue;
    }

    for (const permission of user.permissions) {
      rows.push(
        [
          csvEscape(user.storeHash),
          csvEscape(user.storeName),
          csvEscape(user.id),
          csvEscape(user.email),
          csvEscape(user.firstName),
          csvEscape(user.lastName),
          csvEscape(user.status),
          csvEscape(permission.code),
          csvEscape(permission.accessType),
          csvEscape(user.lastLoginAt),
          csvEscape(user.updatedAt),
        ].join(','),
      );
    }
  }

  return `${[headers.join(','), ...rows].join('\n')}\n`;
}

export function buildMatrixCsvFromRows(
  rows: PermissionMatrixRow[],
  permissionColumns: string[],
): string {
  const headers = ['store_hash', 'store_name', 'email', 'status', ...permissionColumns];
  const dataRows = rows.map((row) =>
    [
      csvEscape(row.storeHash),
      csvEscape(row.storeName),
      csvEscape(row.email),
      csvEscape(row.status),
      ...permissionColumns.map((code) => csvEscape(matrixCellValue(row.cells[code] ?? false))),
    ].join(','),
  );

  return `${[headers.join(','), ...dataRows].join('\n')}\n`;
}

export function downloadFilteredAuditExport(
  kind: 'detail' | 'matrix',
  users: BcStoreUserRecord[],
  matrixRows?: PermissionMatrixRow[],
  permissionColumns?: string[],
): void {
  const date = new Date().toISOString().slice(0, 10);
  if (kind === 'detail') {
    triggerDownload(buildDetailCsvFromUsers(users), `bc-permissions-detail-filtered-${date}.csv`);
    return;
  }

  const columns = permissionColumns ?? buildPermissionMatrix(users).permissionColumns;
  const rows = matrixRows ?? buildPermissionMatrix(users).rows;
  triggerDownload(buildMatrixCsvFromRows(rows, columns), `bc-permissions-matrix-filtered-${date}.csv`);
}
